import { useCallback, useEffect, useRef, useState } from "react";
import { useMapEvents } from "react-leaflet";
import type * as L from "leaflet";
import { useLayers } from "../../context/LayersContext";
import {
  mockLayers,
  defaultDimensionValue,
  wmsDimensionParam,
} from "../../global/constants/layers";
import { geoServerBaseUrl } from "../../services/geoServer";
import { geoServerAuthHeaders } from "../../services/geoServerAuth";
import { useRasterLegend } from "../../hooks/useRasterLegend";
import { useActiveRasterTypename } from "../../hooks/useActiveRasterTypename";
import { cn } from "../../lib/utils";

const THROTTLE_MS = 150;

const UNIT_BY_LAYER_NAME: Record<string, string> = {
  DENSIDADE_POTENCIA_100m: "W/m²",
  MOSAICO_100m_COG: "m",
  UMIDADE_ANUAL: "%",

  // Workspace `aeroespacial`
  mosaico_eolica_velocidade_pe: "m/s",
  mosaico_eolica_velocidade_noronha: "m/s",
  mosaico_eolica_turbulencia_pe: "%",
  mosaico_eolica_direcao_pe: "%",
  mosaico_eolica_direcao_noronha: "%",
  eolica_fernando_de_noronha_100m_densidade_potencia: "W/m²",
  eolica_100m_weibull_a: "m/s",
  eolica_fernando_de_noronha_100m_weibull_a: "m/s",
  eolica_100m_velocidade_referencia_vref_tr50: "m/s",
  eolica_fernando_de_noronha_100m_velocidade_referencia_vref_tr50: "m/s",
  solar_dni_200m_sirgas2000_anual: "kWh/m²/dia",
  solar_dhi_200m_sirgas2000_anual: "kWh/m²/dia",
  solar_ghi_200m_sirgas2000_anual: "kWh/m²/dia",
  solar_insolacao_sirgas2000_200m_anual: "h/dia",
  solar_temperatura_tmax_200m_sirgas2000_anual: "°C",
  solar_precipitacao_media_anual_pe_200m_sirgas2000: "mm",
  solar_precipitacao_dias_chuva_medio_anual_pe_200m_sirgas2000: "dias",
  solar_albedo_sirgas2000_200m_anual: "%",
  solar_albedo_noronha_sirgas2000_200m: "%",
  solar_altimetria_sirgas2000_200m: "m",
  solar_declividade_sirgas2000_200m: "°",
  solar_rugosidade_sirgas2000_200m: "m",
};

function formatValue(value: number): string {
  return value.toLocaleString("pt-BR", { maximumFractionDigits: 1 });
}

export function RasterGradientLegend() {
  const { layers, layerTimeValues, layerElevationValues } = useLayers();

  const activeRaster = layers
    .map((title) => mockLayers.find((l) => l.title === title && l.type === "raster"))
    .find(Boolean);

  // No zoom atual, a camada realmente exibida (ver RenderGeoServerLayers) pode
  // ser a raster detalhada de 30 m, não a base de 200 m em activeRaster.layerName.
  // Legenda e GetFeatureInfo têm que consultar essa mesma camada, senão o valor
  // e a escala de cores mostrados ao usuário não batem com o que está na tela.
  const activeTypename = useActiveRasterTypename(
    activeRaster?.layerName ?? "",
    activeRaster?.detailedLayerName,
    activeRaster?.detailedLayerZoomThreshold
  );

  const legend = useRasterLegend(
    activeRaster?.workspace
      ? { workspace: activeRaster.workspace, layerName: activeTypename }
      : null
  );

  const [hoveredValue, setHoveredValue] = useState<number | null>(null);

  const activeRasterRef = useRef(activeRaster);
  const activeTypenameRef = useRef(activeTypename);
  const legendRef = useRef(legend);
  const layerTimeValuesRef = useRef(layerTimeValues);
  const layerElevationValuesRef = useRef(layerElevationValues);
  useEffect(() => {
    activeRasterRef.current = activeRaster;
  }, [activeRaster]);
  useEffect(() => {
    activeTypenameRef.current = activeTypename;
  }, [activeTypename]);
  useEffect(() => {
    legendRef.current = legend;
  }, [legend]);
  useEffect(() => {
    layerTimeValuesRef.current = layerTimeValues;
  }, [layerTimeValues]);
  useEffect(() => {
    layerElevationValuesRef.current = layerElevationValues;
  }, [layerElevationValues]);

  const generationRef = useRef(0);
  const lastCallAtRef = useRef(0);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Switching/removing the raster, or crossing the detailed-layer zoom
  // threshold, must invalidate any in-flight or scheduled read: a late
  // response would be rendered against the wrong layer's legend/scale.
  useEffect(() => {
    generationRef.current++;
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
    setHoveredValue(null);
  }, [activeTypename]);

  const fetchPixelValue = useCallback((latlng: L.LatLng) => {
    const raster = activeRasterRef.current;
    if (!raster || !legendRef.current) return;
    const typename = activeTypenameRef.current;

    const generation = ++generationRef.current;
    const eps = 0.001;
    const { lat, lng } = latlng;
    const bbox = `${lng - eps},${lat - eps},${lng + eps},${lat + eps}`;

    const params = new URLSearchParams({
      SERVICE: "WMS",
      VERSION: "1.1.1",
      REQUEST: "GetFeatureInfo",
      LAYERS: `${raster.workspace}:${typename}`,
      QUERY_LAYERS: `${raster.workspace}:${typename}`,
      FORMAT: "image/png",
      STYLES: "",
      INFO_FORMAT: "application/json",
      FEATURE_COUNT: "1",
      X: "1",
      Y: "1",
      SRS: "EPSG:4326",
      WIDTH: "3",
      HEIGHT: "3",
      BBOX: bbox,
    });

    if (raster.temporal?.enabled) {
      if (raster.temporal.dimension === "time") {
        const t = layerTimeValuesRef.current[raster.layerName];
        if (t) params.set("TIME", t);
      } else {
        // Same fallback as RenderGeoServerLayers, so the queried slice matches
        // the image actually on screen. Vale para elevation e para as dimensões
        // customizadas dos mosaicos (direction, year), que viajam como DIM_<NOME>.
        const value =
          layerElevationValuesRef.current[raster.layerName] ??
          defaultDimensionValue(raster);
        if (value !== undefined) {
          params.set(
            wmsDimensionParam(raster.temporal.dimension).toUpperCase(),
            String(value)
          );
        }
      }
    }

    fetch(`${geoServerBaseUrl}/${raster.workspace}/wms?${params}`, {
      headers: geoServerAuthHeaders(raster.workspace),
    })
      .then((res) => res.json())
      .then((data) => {
        if (generation !== generationRef.current) return;
        const props = data?.features?.[0]?.properties;
        // Float32 rasters (e.g. the MDT) commonly use ~-3.4e38 as the NoData
        // sentinel; GetFeatureInfo returns it as a plain number, so filter it out.
        const numVal = props
          ? Object.values(props).find(
              (v) => typeof v === "number" && isFinite(v as number) && Math.abs(v as number) < 1e30
            )
          : undefined;
        setHoveredValue(numVal !== undefined ? (numVal as number) : null);
      })
      .catch(() => {
        if (generation === generationRef.current) setHoveredValue(null);
      });
  }, []);

  const scheduleFetch = useCallback(
    (latlng: L.LatLng) => {
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
      const now = Date.now();
      const elapsed = now - lastCallAtRef.current;
      if (elapsed >= THROTTLE_MS) {
        lastCallAtRef.current = now;
        fetchPixelValue(latlng);
      } else {
        pendingTimeoutRef.current = setTimeout(() => {
          lastCallAtRef.current = Date.now();
          fetchPixelValue(latlng);
        }, THROTTLE_MS - elapsed);
      }
    },
    [fetchPixelValue]
  );

  useMapEvents({
    mousemove: (e) => {
      if (!activeRasterRef.current || !legendRef.current) return;
      scheduleFetch(e.latlng);
    },
    mouseout: () => {
      generationRef.current++;
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
      setHoveredValue(null);
    },
  });

  useEffect(() => {
    return () => {
      if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
    };
  }, []);

  if (!legend || !activeRaster) return null;

  // 0% = bottom (min), 100% = top (max) — matches the vertical gradient direction.
  const pct = Math.min(
    100,
    Math.max(0, ((hoveredValue ?? legend.min) - legend.min) / (legend.max - legend.min) * 100)
  );
  const unit = UNIT_BY_LAYER_NAME[activeRaster.layerName];

  const gradientCss = `linear-gradient(to top, ${legend.stops
    .map((s) => `${s.color} ${s.offsetPct}%`)
    .join(", ")})`;

  return (
    <div
      className={cn(
        "mt-2 flex select-none flex-col items-center rounded-[4px] bg-white p-2 shadow-md",
        "pointer-events-auto"
      )}
    >
      <span className="text-[10px] leading-none text-[#707070]">{legend.maxLabel}</span>
      <div
        className="relative my-1 w-2.5 rounded-full"
        style={{ height: 120, background: gradientCss }}
      >
        {hoveredValue !== null && (
          <>
            <div
              className="absolute left-1/2 h-0.5 w-4 -translate-x-1/2 translate-y-1/2 rounded-sm bg-black/70"
              style={{ bottom: `${pct}%` }}
            />
            <div
              data-testid="raster-hover-value"
              className="absolute left-full ml-1.5 translate-y-1/2 whitespace-nowrap rounded-[4px] bg-black/80 px-1.5 py-0.5 text-[10px] leading-none text-white"
              style={{ bottom: `${pct}%` }}
            >
              {formatValue(hoveredValue)}
              {unit ? ` ${unit}` : ""}
            </div>
          </>
        )}
      </div>
      <span className="text-[10px] leading-none text-[#707070]">{legend.minLabel}</span>
    </div>
  );
}

export default RasterGradientLegend;
